import { useEditor } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildEditorExtensions } from './extensions';
import { useSlashCommand, } from './extensions/slash-command';
const defaultOptions = {};
const defaultDependencies = [];
const DEFAULT_EMPTY_LINE_PLACEHOLDER_TEXT = "Write, type '/' for commands…";
/**
 * Escapes a string for safe use in CSS custom property string values.
 * Prevents CSS injection by escaping special characters including:
 * - Backslashes (must be escaped first)
 * - Double quotes and single quotes
 * - Backticks
 * - Newlines and other control characters (0x00-0x1F, 0x7F)
 */
function escapeCssString(value) {
    return value
        .replace(/\\/g, '\\\\') // Escape backslashes first
        .replace(/"/g, '\\"') // Escape double quotes
        .replace(/'/g, "\\'") // Escape single quotes
        .replace(/`/g, '\\`') // Escape backticks
        .split('')
        .map((char) => {
        const code = char.charCodeAt(0);
        // Handle control characters (0x00-0x1F) and DEL (0x7F)
        if ((code >= 0x00 && code <= 0x1f) || code === 0x7f) {
            if (code === 0x0a)
                return ' '; // Newline -> space
            if (code === 0x0d)
                return ' '; // Carriage return -> space
            if (code === 0x09)
                return ' '; // Tab -> space
            return ''; // Remove other control characters
        }
        return char;
    })
        .join('');
}
export const useRichTextEditor = ({ onSubmit, triggerSubmit, uploadFile, onError, editorOptions = defaultOptions, className = '', dependencies = defaultDependencies, linkOptions = defaultOptions, placeholderOptions, watchContent, mentionsPopupParams, mentionClassName, slashCommandOptions, }) => {
    const emptyLinePlaceholderText = placeholderOptions?.emptyLinePlaceholderText ?? DEFAULT_EMPTY_LINE_PLACEHOLDER_TEXT;
    const editorRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const { extension: slashCommandExtension, controller: slashCommandController } = useSlashCommand(slashCommandOptions);
    const handleSubmit = useCallback(async () => {
        setIsLoading(true);
        if (editorRef.current?.isEmpty) {
            // Empty editor still has `<p></p>` content returned, so in that case we switch its return value with an empty string
            // to trigger external submit validation.
            await onSubmit('', null);
            setIsLoading(false);
            return;
        }
        const content = editorRef.current?.getHTML();
        if (content) {
            await onSubmit(content, editorRef.current);
            if (editorOptions?.clearOnSubmit) {
                editorRef.current?.commands.clearContent();
            }
        }
        setIsLoading(false);
    }, [editorOptions?.clearOnSubmit, onSubmit]);
    const mentionsParamsRef = useRef(mentionsPopupParams);
    useEffect(() => {
        mentionsParamsRef.current = mentionsPopupParams;
    }, [mentionsPopupParams]);
    const extensions = useMemo(() => {
        const base = buildEditorExtensions({
            mode: 'editor',
            onError,
            uploadFile,
            linkOptions,
            placeholderOptions,
            mentionsPopupParamsRef: mentionsParamsRef,
            mentionClassName,
        });
        if (slashCommandExtension) {
            base.push(slashCommandExtension);
        }
        if (editorOptions.extensions) {
            base.push(...editorOptions.extensions);
        }
        return base;
    }, [
        onError,
        uploadFile,
        linkOptions,
        placeholderOptions,
        mentionClassName,
        editorOptions.extensions,
        slashCommandExtension,
    ]);
    const editor = useEditor({
        ...editorOptions,
        extensions,
    }, [editorOptions.editable, ...dependencies]);
    useEffect(() => {
        // Setting keydown handler in an effect because we don't have editor reference earlier.
        if (!editor) {
            return;
        }
        editorRef.current = editor;
        editor.setOptions({
            editorProps: {
                attributes: {
                    class: ['portal-editor', className].join(' '),
                    // Native browser spellcheck (OS dictionary). Explicit so contenteditable
                    // underlines typos while typing without a custom TipTap extension.
                    spellcheck: 'true',
                    style: `--empty-line-placeholder-text: "${escapeCssString(emptyLinePlaceholderText)}"`,
                },
                handleDOMEvents: {
                    keydown: (_view, e) => {
                        if (e.key === 'Enter' && e.metaKey && editor) {
                            e.preventDefault();
                            triggerSubmit?.();
                        }
                        if (e.key === 'u' && e.metaKey && !e.shiftKey && editor) {
                            e.preventDefault();
                            editor.commands.toggleUnderline();
                        }
                    },
                },
            },
        });
    }, [
        editor,
        triggerSubmit,
        onSubmit,
        editorOptions?.clearOnSubmit,
        className,
        emptyLinePlaceholderText,
    ]);
    useEffect(() => {
        if (!editor || typeof editorOptions.content !== 'string') {
            return;
        }
        if (watchContent && editorOptions.content !== editor.getHTML()) {
            editor.commands.setContent(editorOptions.content);
        }
    }, [editorOptions.content, editor, watchContent]);
    return { editor, handleSubmit, isLoading, slashCommand: slashCommandController };
};
